/*  ===========================================================
    src/Pages/CalendarView.js  (month endpoint version)
    -----------------------------------------------------------
    • Fetches only current month via /api/calendar?from&to
    • Drag-and-drop re-dates items, then refetches month
    • Shows Spotify badge using server-supplied mapping
    • "+" per day opens ReleaseModal prefilled with that day
    • Today’s items are not dimmed (only strictly past dates)
    ======================================================== */

import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api';
import emptyArt from '../assets/emptyart.jpg';
import spotifyIcon from '../assets/icons/spotify.svg';
import ReleaseModal from '../Components/ReleaseModal';
import { CircularProgressbar, CircularProgressbarWithChildren, buildStyles } from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';
import infoIcon from '../assets/icons/info.svg';
import ReleaseInfocard from '../Components/ReleaseInfocard';
import { useAudio } from '../contexts/AudioContext';


/* ───────── helpers ───────── */
const pad = (n) => String(n).padStart(2, '0');
const ymd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const dim = (y, m) => new Date(y, m + 1, 0).getDate();
const first = (y, m) => (new Date(y, m, 1).getDay() + 6) % 7; // 0..6, Monday first

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const MONTHS = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December'
];

// Departments we track progress for (must match your template departments)
const DEPARTMENTS = ['Operations', 'Marketing', 'Socials+Creative', 'A&R'];

// Normalize old/odd tag values to the canonical labels used in TAG_COLORS
const normalizeTag = (val) => {
  const t = String(val || '').trim().toLowerCase();
  switch (t) {
    case 'single': return 'Single';
    case 'focus single':
    case 'focussingle': return 'Focus Single';
    case 'impact date':
    case 'impact': return 'Impact Date';
    case 're-release':
    case 'rerelease':
    case 're release': return 'Re-Release';
    case 'album': return 'Album';
    case 'ep': return 'EP';
    default: return ''; // unknown tag → don't render
  }
};

const TAG_COLORS = {
  Album: '#31576c',
  EP: '#583e72',
  Single: '#555',
  'Impact Date': '#555',
  'Focus Single': '#f3b40b',
  'Re-Release': '#78be1e',
};

const STATUS_COLORS = {
  Confirmed: '#28a745', // green
  Hold: '#f39c12',      // orange
};

/* ► header arrow button */
const Arrow = ({ children, onClick }) => (
  <button
    onClick={onClick}
    style={{
      background: '#333',
      color: '#fff',
      border: 'none',
      borderRadius: 4,
      width: 32,
      height: 32,
      cursor: 'pointer',
      fontSize: 18,
      lineHeight: '32px',
    }}
  >
    {children}
  </button>
);

/* ► card */
const Card = React.memo(
  function Card({
    item,
    artistStr,
    tag,
    status,
    onToggleStatus,
    indent = false,
    dimmed = false,
    spotify = null,
    deptProg = null,
    onDragStart,
    onClick,
    onInfo,
    onPlayAudio,
    isPlaying = false,
  }) {
    // Reserve space on the right so text never sits under badges
const progCount   = deptProg ? Object.keys(deptProg).length : 0;
const dotSize     = 17;   // each progress dot size
const dotGap      = 4;    // gap between dots
const dotsWidth   = progCount ? (progCount * dotSize) + ((progCount - 1) * dotGap) : 0;

// right edge padding
const rightPadding = 8;

// badges (rightmost → left): INFO • gap • PROGRESS DOTS • gap • SPOTIFY
// info only for releases
const showInfo    = item.itemType === 'release';
const gap         = 6;
const infoSize    = showInfo ? 19 : 0;       // match your current button width (19)
const spotifySize = spotify ? 21 : 0;

// rightmost: INFO
const infoRight     = rightPadding;

// to the left of info: PROGRESS DOTS (if any)
const progressRight = infoRight + (showInfo ? (infoSize + gap) : 0);

// to the left of progress: SPOTIFY (if any)
const spotifyRight  = progressRight + (progCount ? (dotsWidth + gap) : 0);

// reserve text gutter for everything visible on the right
const rightGutter =
  rightPadding +
  (showInfo ? (infoSize + gap) : 0) +
  (progCount ? (dotsWidth + gap) : 0) +
  (spotify ? spotifySize : 0);

// vertical space so badges don't overlap first line
const badgeRowHeight = (showInfo || progCount || spotify) ? 24 : 0;
    return (
      <div
        draggable
        onDragStart={onDragStart}
        onClick={onClick}
        style={{
          position: 'relative',
          display: 'flex',
          gap: 6,
          alignItems: 'center',
          background: '#2d2d2d',
          borderRadius: 4,
          padding: 10,
          marginTop: indent ? 6 : 8,
          marginLeft: indent ? 4 : 0,
          borderLeft: indent ? '3px solid #555' : 'none',
          opacity: dimmed ? 0.4 : 1,
          cursor: 'pointer',
        }}
      >
{/* Department progress (now on the right) */}
{deptProg && (
  <div
    style={{
      position: 'absolute',
      top: 4,
      right: 34,                     
      display: 'flex',
      gap: 4,
    }}
  >
    {Object.entries(deptProg).map(([dep, val]) => {
      const pct = Math.max(0, Math.min(100, val ?? 0));
      const full = pct >= 99.9; // treat as 100%

      return (
        <div key={dep} style={{ width: 17, height: 17 }}>
          {full ? (
            <CircularProgressbarWithChildren
              value={100}
              background
              backgroundPadding={0}
              strokeWidth={12}
              styles={buildStyles({
                backgroundColor: '#7eef4f', // fill the whole circle
                trailColor: '#7eef4f',      // match background so ring looks filled
                pathColor: '#7eef4f',       // keep the path same color
                strokeLinecap: 'butt',
              })}
            >
              {/* centered checkmark */}
              <svg viewBox="0 0 24 24" width="12" height="12" style={{ display: 'block' }}>
                <path
                  d="M5 13l4 4L19 7"
                  fill="none"
                  stroke="#1e1e1e"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </CircularProgressbarWithChildren>
          ) : (
            <CircularProgressbar
              value={pct}
              strokeWidth={12}
              styles={buildStyles({
                pathColor: '#7eef4f',
                trailColor: '#3E3E3E',
                strokeLinecap: 'butt',
              })}
            />
          )}
        </div>
      );
    })}
  </div>
)}
{/* Info button (left of Spotify & dots) */}
{item.itemType === 'release' && (
  <button
    type="button"
    onClick={(e) => {
      e.stopPropagation();
      onInfo && onInfo(item);
    }}
    title="Release info"
    style={{
      position: 'absolute',
      top: 4,
      right: infoRight,
      width: 19,
      height: 21,
      background: 'transparent',
      border: 'none',
      padding: 0,
      cursor: 'pointer',
    }}
  >
    <img
      src={infoIcon}
      alt="Info"
      style={{
        width: 18,
        height: 18,
        filter: 'invert(70%)', // more visible gray
        opacity: 1,
        display: 'block',
      }}
    />
  </button>
)}

{/* Spotify badge (just left of progress) */}
{spotify && (
  <a
    href={`https://open.spotify.com/${spotify.kind}/${spotify.id}`}
    target="_blank"
    rel="noreferrer"
    onClick={(e) => e.stopPropagation()}
    style={{ position: 'absolute', top: 4, right: spotifyRight }}
  >
    <img
      src={spotifyIcon}
      alt="Spotify"
      style={{
        width: 21,
        height: 21,
        filter:
          'invert(81%) sepia(68%) saturate(500%) hue-rotate(45deg) brightness(103%) contrast(97%)',
      }}
    />
  </a>
)}

        {/* Cover art with play button */}
        <div
          style={{
            position: 'relative',
            width: 40,
            height: 40,
            flexShrink: 0,
          }}
        >
          <img
            src={item.coverArt || emptyArt}
            alt=""
            loading="lazy"
            style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4, display: 'block' }}
          />
          
          {/* Play button overlay */}
          {onPlayAudio && (
            <div
              onClick={(e) => {
                e.stopPropagation();
                onPlayAudio(item);
              }}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'rgba(0, 0, 0, 0.6)',
                borderRadius: 4,
                opacity: isPlaying ? 0.9 : 0,
                transition: 'opacity 0.2s',
                cursor: 'pointer',
                zIndex: 1,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.opacity = '0.9';
              }}
              onMouseLeave={(e) => {
                if (!isPlaying) e.currentTarget.style.opacity = '0';
              }}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="white"
                style={{ filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.5))' }}
              >
                {isPlaying ? (
                  // Pause icon
                  <>
                    <rect x="6" y="4" width="4" height="16" />
                    <rect x="14" y="4" width="4" height="16" />
                  </>
                ) : (
                  // Play icon
                  <path d="M8 5v14l11-7z" />
                )}
              </svg>
            </div>
          )}
        </div>

<div
  style={{
    fontSize: 12,
    lineHeight: 1.2,
    color: '#ddd',
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
    overflow: 'hidden',
  }}
>
  {/* Only the top text needs the right gutter for the badges */}
  <div style={{ paddingRight: rightGutter, minWidth: 0 }}>
    <strong
      style={{
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
        overflow: 'hidden',
        display: 'block',
      }}
    >
      {item.title}
    </strong>
    <span
      style={{
        color: '#999',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
        overflow: 'hidden',
        display: 'block',
      }}
    >
      {artistStr}
    </span>
  </div>

{/* Type + Status row */}
{(tag || status) && (
  <div
    style={{
      display: 'flex',
      gap: 4,
      marginTop: 2,
      flexWrap: 'nowrap',     // one line only
      overflow: 'hidden',     // allow inner chip to ellipsize
      alignItems: 'center',
    }}
  >
    {tag && (
      <div
        style={{
          fontSize: 10,
          fontWeight: 'bold',
          background: TAG_COLORS[tag] || '#555',
          padding: '2px 4px',
          borderRadius: 4,
          color: tag === 'Focus Single' ? '#111' : '#fff',
          display: 'inline-flex',
          alignItems: 'center',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          minWidth: 0,
          flex: '0 1 auto',        // don't grow; only shrink (ellipsis) when tight
maxWidth: '100%', 
        }}
      >
        {tag}
      </div>
    )}
    {status && (
      <div
        onClick={(e) => { e.stopPropagation(); onToggleStatus && onToggleStatus(); }}
        title={`Click to set ${status === 'Confirmed' ? 'Hold' : 'Confirmed'}`}
        style={{
          fontSize: 10,
          fontWeight: 'bold',
          background: STATUS_COLORS[status] || '#555',
          padding: '2px 4px',
          borderRadius: 4,
          color: '#fff',
          cursor: 'pointer',
          userSelect: 'none',
          opacity: onToggleStatus ? 1 : 0.7,
          display: 'inline-flex',
          alignItems: 'center',
          whiteSpace: 'nowrap',
          flex: '0 0 auto',        // don’t shrink the status chip
        }}
      >
        {status}
      </div>
    )}
  </div>
)}

        </div>
      </div>
    );
  },
  (a, b) =>
  a.item._id === b.item._id &&
  a.item.itemType === b.item.itemType &&
  a.artistStr === b.artistStr &&
  a.tag === b.tag &&
  a.status === b.status &&
  a.indent === b.indent &&
  a.dimmed === b.dimmed &&
  a.isPlaying === b.isPlaying &&
  ((a.spotify?.id || '') === (b.spotify?.id || '')) &&
  ((a.spotify?.kind || '') === (b.spotify?.kind || '')) &&
  JSON.stringify(a.deptProg) === JSON.stringify(b.deptProg)
);

/* ───────── main component ───────── */
export default function CalendarView() {
  const nav = useNavigate();
  const { playTrack, currentTrack, togglePlayPause, isPlaying: globalIsPlaying } = useAudio();
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  /* month pager */
  // Restore last-viewed month from localStorage if present; else default to current month
const [cur, setCur] = useState(() => {
  try {
    const saved = localStorage.getItem('cal:last');
    if (saved) {
      // saved format: "YYYY-M" (M is 0-based month)
      const [yy, mm] = saved.split('-').map((n) => parseInt(n, 10));
      if (!Number.isNaN(yy) && !Number.isNaN(mm)) {
        return new Date(yy, mm, 1);
      }
    }
  } catch {}
  return new Date(today.getFullYear(), today.getMonth(), 1);
});

  

  /* month data from server */
  const [days, setDays] = useState({}); // { 'YYYY-MM-DD': [items] }
  const [artists, setArtists] = useState({}); // { artistId: name }
  const [spotify, setSpotify] = useState({}); // { releaseId: {id,kind} }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  /* modal state + prefill date */
  const [addOpen, setAddOpen] = useState(false);
  const [addDate, setAddDate] = useState(''); // YYYY-MM-DD
  const [allArtists, setAllArtists] = useState([]);
  const [allProjects, setAllProjects] = useState([]);

  // Per-release department progress cache + loading flags
const [deptProgress, setDeptProgress] = useState({});   // { [releaseId]: { Operations: %, ... } }
const [progressLoading, setProgressLoading] = useState({}); // { [releaseId]: true }

// Inline status toggle (busy guard per release)
const [statusBusy, setStatusBusy] = useState({}); // { [releaseId]: true }
// Infocard state
const [infoOpen, setInfoOpen] = useState(false);
const [infoRelease, setInfoRelease] = useState(null);

// Year releases count
const [yearReleasesCount, setYearReleasesCount] = useState(0);
const [loadingYearCount, setLoadingYearCount] = useState(false);

const toggleStatus = async (releaseId, dateKey, current) => {
  const id = String(releaseId);
  if (statusBusy[id]) return;

  const next = current === 'Confirmed' ? 'Hold' : 'Confirmed';

  // Optimistic update
  setStatusBusy(b => ({ ...b, [id]: true }));
  setDays(prev => {
    const copy = { ...prev };
    const dayArr = Array.isArray(copy[dateKey]) ? [...copy[dateKey]] : [];
    copy[dateKey] = dayArr.map(item =>
      String(item._id) === id && item.itemType === 'release'
        ? { ...item, releaseStatus: next }
        : item
    );
    return copy;
  });

  try {
    const r = await api(`/api/releases/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ releaseStatus: next }),
    });
    if (!r.ok) {
      throw new Error(`HTTP ${r.status}`);
    }
  } catch (e) {
    // Revert on failure
    setDays(prev => {
      const copy = { ...prev };
      const dayArr = Array.isArray(copy[dateKey]) ? [...copy[dateKey]] : [];
      copy[dateKey] = dayArr.map(item =>
        String(item._id) === id && item.itemType === 'release'
          ? { ...item, releaseStatus: current }
          : item
      );
      return copy;
    });
    alert('Could not update status.');
  } finally {
    setStatusBusy(b => {
      const c = { ...b };
      delete c[id];
      return c;
    });
  }
};

// Open infocard for a calendar item (releases only)
const openInfoFor = async (it) => {
  try {
    if (it.itemType !== 'release') return;
    const r = await api(`/api/releases/${it._id}`);
    if (!r.ok) throw new Error(`Fetch release failed (${r.status})`);
    const full = await r.json();
    setInfoRelease(full);
    setInfoOpen(true);
  } catch (e) {
    alert(e.message || 'Could not load release info');
  }
};

const monthAbort = useRef(null);

/* fetch month */
const fetchMonth = async (d) => {
  // cancel any in-flight month request
  if (monthAbort.current) monthAbort.current.abort();
  monthAbort.current = new AbortController();
  const { signal } = monthAbort.current;
    setLoading(true);
    setError('');
    const y = d.getFullYear();
    const m = d.getMonth();
    const from = `${y}-${String(m + 1).padStart(2, '0')}-01`;
    const to = `${y}-${String(m + 1).padStart(2, '0')}-${String(dim(y, m)).padStart(2, '0')}`;
    try {
      const r = await api(`/api/calendar?from=${from}&to=${to}`, { signal });
      if (!r.ok) throw new Error('API error');
      const data = await r.json(); // { days, artists, spotify }
      setDays(data.days || {});
      setArtists(data.artists || {});
      setSpotify(data.spotify || {});
    } catch (e) {
  if (e.name !== 'AbortError') setError(e.message || 'error');
} finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMonth(cur);
  }, [cur]);

  // Fetch year releases count when year changes
  useEffect(() => {
    const fetchYearReleases = async () => {
      const y = cur.getFullYear();
      const from = `${y}-01-01`;
      const to = `${y}-12-31`;
      setLoadingYearCount(true);
      try {
        const r = await api(`/api/calendar?from=${from}&to=${to}`);
        if (!r.ok) throw new Error('API error');
        const data = await r.json();
        // Count only releases (not projects)
        let count = 0;
        for (const dayItems of Object.values(data.days || {})) {
          if (Array.isArray(dayItems)) {
            count += dayItems.filter(item => item.itemType === 'release').length;
          }
        }
        setYearReleasesCount(count);
      } catch (e) {
        console.error('Failed to fetch year releases count:', e);
        setYearReleasesCount(0);
      } finally {
        setLoadingYearCount(false);
      }
    };
    fetchYearReleases();
  }, [cur.getFullYear()]);

  // Save last-viewed month to localStorage whenever it changes
useEffect(() => {
  try {
    localStorage.setItem('cal:last', `${cur.getFullYear()}-${cur.getMonth()}`); // month is 0-based
  } catch {}
}, [cur]);

  /* fetch modal lookups (artists/projects) lazily once */
  useEffect(() => {
    (async () => {
      try {
        const [a, p] = await Promise.all([api('/api/artists'), api('/api/projects')]);
        setAllArtists(a.ok ? await a.json() : []);
        setAllProjects(p.ok ? await p.json() : []);
      } catch {
        setAllArtists([]);
        setAllProjects([]);
      }
    })();
  }, []);

  /* name lookup */
  const nameOfId = (id) => artists[String(typeof id === 'object' ? id._id : id)] || null;

  // Compute department % from categories+tasks
const computeDeptProgress = (cats = [], tasks = []) => {
  const out = {};
  for (const dep of DEPARTMENTS) {
    const depCatIds = cats.filter(c => c.department === dep).map(c => String(c._id));
    const depTasks  = tasks.filter(t => depCatIds.includes(String(t.categoryId)));
    if (!depTasks.length) continue; // skip bars for departments with no tasks
    const done = depTasks.filter(t => t.status === 'Completed').length;
    out[dep] = (done / depTasks.length) * 100;
  }
  return out;
};

// Lazy-load a release's progress if not cached
const fetchDeptProgress = async (item) => {
  const id = String(item._id);
  if (progressLoading[id] || deptProgress[id]) return;

  setProgressLoading(p => ({ ...p, [id]: true }));
  try {
    // Use preloaded data from calendar payload if present
    const cats  = item.releaseCategories || null;
    const tasks = item.releaseTasks || null;

    let prog;
    if (Array.isArray(cats) && Array.isArray(tasks)) {
      prog = computeDeptProgress(cats, tasks);
    } else {
      // Fallback: fetch the release
      const r = await api(`/api/releases/${id}`);
      if (!r.ok) throw new Error(`fetch release ${r.status}`);
      const rel = await r.json();
      prog = computeDeptProgress(rel.releaseCategories || [], rel.releaseTasks || []);
    }

    setDeptProgress(p => ({ ...p, [id]: prog }));
  } catch {
    // ignore; just don't show bars
  } finally {
    setProgressLoading(p => {
      const copy = { ...p };
      delete copy[id];
      return copy;
    });
  }
};

  /* build month grid ---------------------------------------------- */
  const y = cur.getFullYear(),
    m = cur.getMonth();
  const offs = first(y, m);
  const raw = Array.from({ length: 42 }, (_, i) => {
    const d = new Date(y, m, 1 - offs + i);
    d.setHours(0, 0, 0, 0);
    return { date: d, key: ymd(d), inMonth: d.getMonth() === m };
  });
  const cells = raw
    .reduce((rows, c, i) => (rows[Math.floor(i / 7)] ??= []).push(c) && rows, [])
    .filter((r) => r.some((c) => c.inMonth))
    .flat();

  /* drag-n-drop ---------------------------------------------------- */
  const dragRef = useRef(null);
  const startDrag = (e, item) => {
    dragRef.current = item;
    e.dataTransfer.effectAllowed = 'move';
  };
  const over = (e) => e.preventDefault();

  const drop = async (e, targetDate) => {
    e.preventDefault();
    const drag = dragRef.current;
    if (!drag) return;
    const newISO = ymd(targetDate);

    try {
      if (drag.type === 'release') {
        await api(`/api/releases/${drag.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ releaseDate: newISO }),
        });
      } else {
        const projId = drag.id;
        await api(`/api/projects/${projId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ releaseDate: newISO }),
        });
      }
      fetchMonth(cur);
    } catch (err) {
      console.error(err);
      alert('Could not update date');
    }
    dragRef.current = null;
  };

  /* add modal helpers ----------------------------------------------- */
  const openAddModal = (dateKey) => {
    setAddDate(dateKey); // e.g. "2025-09-12"
    setAddOpen(true);
  };

  const handleCreateRelease = async (payload) => {
    const body = { ...payload };
    if (!body.releaseDate) body.releaseDate = addDate; // fill if modal didn’t
    try {
      const r = await api('/api/releases', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!r.ok) {
        const t = await r.text();
        throw new Error(`Create failed (${r.status}): ${t}`);
      }
      setAddOpen(false);
      fetchMonth(cur);
    } catch (e) {
      alert(e.message || 'Failed to create release');
    }
  };

  /* audio player handler */
  const handlePlayAudio = async (item) => {
    // Only play releases with MP3 Masters
    if (item.itemType !== 'release' || !item.uploads?.mp3Master) return;

    // If this track is already loaded, just toggle play/pause
    if (currentTrack?.releaseId === item._id) {
      togglePlayPause();
      return;
    }

    const mp3Key = item.uploads.mp3Master;
    let mp3Url = null;

    // If it's already a full URL, use it directly
    if (/^https?:\/\//i.test(mp3Key)) {
      mp3Url = mp3Key;
    } else {
      // Otherwise, get a signed URL from the backend
      try {
        const res = await api(`/api/assets/sign-get?key=${encodeURIComponent(mp3Key)}`);
        if (!res.ok) throw new Error('Failed to get signed URL');
        const data = await res.json();
        mp3Url = data.url;
      } catch (err) {
        console.error('Failed to fetch MP3 signed URL:', err);
        alert('Could not load audio file');
        return;
      }
    }

    // Get artist names
    const artistIds = Array.isArray(item.artists) ? item.artists : [];
    const artistNames = artistIds.map(id => nameOfId(id)).filter(Boolean);

    playTrack({
      audioUrl: mp3Url,
      trackTitle: item.title,
      artistName: artistNames.join(', ') || 'Unknown Artist',
      coverArt: item.coverArt || emptyArt,
      releaseId: item._id,
    });
  };

  /* month nav */
  const prev = () => setCur((d) => new Date(d.getFullYear(), d.getMonth() - 1, 1));
  const next = () => setCur((d) => new Date(d.getFullYear(), d.getMonth() + 1, 1));

  /* ───────── render ───────── */
  return (
    <div style={{ padding: 20 }}>
      {/* CSS for hover reveal of the "+" button */}
      <style>{`
  .day-add-btn { 
    opacity: 0; 
    transition: opacity .15s; 
  }
  .cal-cell:hover .day-add-btn { 
    opacity: 1; 
  }
`}</style>

      <header
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          margin: '10px 0 10px',
        }}
      >
        <div>
          <h1 className="header" style={{ margin: 0 }}>
            {MONTHS[m]} {y}
          </h1>
          {/* Stats display */}
          {(() => {
            // Count releases for current month (calculate once)
            let monthCount = 0;
            for (const dayItems of Object.values(days)) {
              if (Array.isArray(dayItems)) {
                monthCount += dayItems.filter(item => item.itemType === 'release').length;
              }
            }
            return (
              <div style={{ 
                display: 'flex', 
                gap: 12, 
                marginTop: 12,
                alignItems: 'center',
              }}>
                <div style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  background: '#2a2a2a',
                  padding: '6px 12px',
                  borderRadius: 8,
                  fontSize: 13,
                  color: '#ddd',
                  fontWeight: 500,
                }}>
                  <span style={{ color: '#7eef4f', marginRight: 6, fontWeight: 600 }}>
                    {monthCount}
                  </span>
                  <span style={{ color: '#999' }}>
                    {monthCount === 1 ? 'release' : 'releases'} this month
                  </span>
                </div>
                <div style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  background: '#2a2a2a',
                  padding: '6px 12px',
                  borderRadius: 8,
                  fontSize: 13,
                  color: '#ddd',
                  fontWeight: 500,
                }}>
                  <span style={{ color: '#7eef4f', marginRight: 6, fontWeight: 600 }}>
                    {loadingYearCount ? '...' : yearReleasesCount}
                  </span>
                  <span style={{ color: '#999' }}>
                    {loadingYearCount ? '' : `${yearReleasesCount === 1 ? 'release' : 'releases'} in ${y}`}
                  </span>
                </div>
              </div>
            );
          })()}
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <button
            onClick={() => {
              const now = new Date();
              now.setHours(0, 0, 0, 0);
              setCur(new Date(now.getFullYear(), now.getMonth(), 1)); // jump to current month
            }}
            style={{
              background: '#333',
              color: '#fff',
              border: 'none',
              borderRadius: 4,
              height: 32,
              padding: '0 10px',
              cursor: 'pointer',
              fontSize: 13,
              fontWeight: 600,
            }}
            title="Jump to current month"
          >
            Today
          </button>

          <Arrow onClick={prev}>‹</Arrow>
          <Arrow onClick={next}>›</Arrow>
        </div>
      </header>

      {loading && <p style={{ color: '#999' }}>Loading…</p>}
      {error && <p style={{ color: 'salmon' }}>{error}</p>}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7,1fr)',
          gap: 1,
          background: '#333',
          borderRadius: 6,
          overflow: 'hidden',
        }}
      >
        {WEEKDAYS.map((w) => (
          <div
            key={w}
            style={{
              background: '#2a2a2a',
              color: '#bbb',
              textAlign: 'center',
              fontWeight: 'bold',
              padding: '6px 0',
              fontSize: 14,
            }}
          >
            {w}
          </div>
        ))}

        {cells.map(({ date, key, inMonth }) => {
          const list = days[key] || [];
          const isToday = date.getTime() === new Date(new Date().setHours(0, 0, 0, 0)).getTime();

          return (
            <div
              key={key}
              className="cal-cell"
              onDragOver={over}
              onDrop={(e) => drop(e, date)}
              style={{
                background: inMonth ? '#1e1e1e' : '#242424',
                minHeight: 120,
                padding: '24px 6px 6px', // top padding so "+" never overlaps cards
                position: 'relative',
                border: isToday ? '2px solid #7eef4f' : '1px solid #2a2a2a',
                overflowY: 'auto',
              }}
            >
              {/* day number */}
              <span
                style={{
                  position: 'absolute',
                  top: 6,
                  right: 8,
                  fontSize: 12,
                  color: inMonth ? '#888' : '#666',
                }}
              >
                {date.getDate()}
              </span>

              {/* add button (hover reveal) */}
              <button
                onClick={() => openAddModal(key)}
                title="Add release on this day"
                className="day-add-btn"
                style={{
                  position: 'absolute',
                  top: 4,
                  left: 6,
                  width: 22,
                  height: 22,
                  borderRadius: 6,
                  background: '#2c2c2c',
                  border: '1px solid #3a3a3a',
                  color: '#fff',
                  lineHeight: '20px',
                  fontWeight: 700,
                  cursor: 'pointer',
                  zIndex: 3,
                  transition: 'opacity .15s',
                }}
              >
                +
              </button>

              {list.map((it) => {
                // strictly past → dim; today stays bright
                const itDate = new Date(it.releaseDate);
                itDate.setHours(0, 0, 0, 0);
                const dimmed = itDate.getTime() < today.getTime();

                const idArr = Array.isArray(it.artists) ? it.artists : [];
                const names = idArr.map(nameOfId).filter(Boolean).join(', ') || 'No Artist';
                const rawTag = it.itemType === 'project' ? it.projectType : it.releaseType;
const tag = normalizeTag(rawTag);
                const sp = spotify[String(it._id)] || null;
                // Ensure we have department progress in cache (only for releases)
let prog = null;
if (it.itemType === 'release') {
  const id = String(it._id);
  prog = deptProgress[id] || null;
  if (!prog && !progressLoading[id]) {
    fetchDeptProgress(it); // lazy load once
  }
}

                return (
                  <Card
                    key={it._id}
                    item={it}
                    artistStr={names}
                    tag={tag}
                    status={it.itemType === 'release' ? (it.releaseStatus || '') : ''}
                    onToggleStatus={
    it.itemType === 'release'
      ? () => toggleStatus(it._id, key, it.releaseStatus || '')
      : undefined
  }
                    indent={Boolean(it.indent)}
                    dimmed={dimmed}
                    spotify={sp}
                    deptProg={prog}
                    onDragStart={(e) =>
                      startDrag(e, {
                        id: it._id,
                        type: it.itemType,
                        oldDate:
                          typeof it.releaseDate === 'string'
                            ? it.releaseDate.slice(0, 10)
                            : '',
                      })
                    }
                    onClick={() =>
  nav(
    it.itemType === 'release' ? `/release/${it._id}` : `/project/${it._id}`,
    {
      // tell the details page we came from the calendar (and which day)
      state: { from: 'calendar', dateKey: key }
    }
  )
}
onInfo={openInfoFor}
                    onPlayAudio={it.itemType === 'release' && it.uploads?.mp3Master ? handlePlayAudio : undefined}
                    isPlaying={currentTrack?.releaseId === it._id && globalIsPlaying}
                  />
                );
              })}
            </div>
          );
        })}
      </div>

      {/* Add Release Modal (prefilled date) */}
      <ReleaseModal
  open={addOpen}
  onCancel={() => setAddOpen(false)}
  onSave={handleCreateRelease}
  allArtists={allArtists}
  allProjects={allProjects}
  onArtistsRefresh={async () => {
    try {
      const a = await api('/api/artists');
      if (a.ok) setAllArtists(await a.json());
    } catch (_) {}
  }}
  defaultReleaseDate={addDate} // 👈 prefill date shown in the modal
/>
<ReleaseInfocard
  open={infoOpen}
  onClose={() => setInfoOpen(false)}
  release={infoRelease}
/>
    </div>
  );
}